import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TFormType } from 'src/@types';
import { RHFSelect } from 'src/components/hook-form';
import { UploadErrorMessage } from 'src/components/hook-form/UploadErrorMessage';
import RHFUploadImage from 'src/components/hook-form/uploadImage/RHFUploadImage';
import useGetSelectOption from 'src/hooks/useGetSelectOption';
import { FormInnerContainer, FormItem, FormLabel, FormRow } from 'src/layouts/form/FormLayout';
import { useStores } from 'src/models';
import { FormType } from 'src/static/constants/common/form';

interface CommonProps {
  formType: TFormType;
}
export const Common = observer(({ formType }: CommonProps) => {
  const { t } = useTranslation();
  const isFirstRender = useRef(true);
  const { getUseYn, getLang } = useGetSelectOption();
  const { categoryStore } = useStores();
  const {
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();

  // 썸네일 파일
  const [file, setFile] = useState<Array<File | string>>([]);

  // 최상위 카테고리 제외하고 2epth 이하 카테고리만 노출 select option
  const CTEGRY_SELECT_OPTIONS = categoryStore.categories
    .filter((item) => item.pCategory !== null)
    .map((item) => ({
      label: item.categoryNm,
      value: item.categorySid,
    }));

  // 카테고리에 따른 파이프라인 선택 옵션
  const PIPELINE_SELECT_OPTIONS = () => {
    const selectCategory = categoryStore.categories.find(
      (item) => item.categorySid === watch('bnSubCatCd'),
    );
    // pipelineList가 없을 경우 기본 옵션 반환
    if (!selectCategory?.pipelineList || selectCategory.pipelineList.length === 0) {
      return [{ value: 0, label: '없음' }];
    }

    // pipelineList가 존재하면 `\n`으로 분리하고 각 항목을 옵션으로 변환
    return selectCategory.pipelineList
      .split('\n')
      .filter((pipeline) => pipeline.trim() !== '') // 빈 항목 제거
      .map((pipeline) => ({
        label: pipeline,
        value: pipeline,
      }));
  };

  useEffect(() => {
    // 상세 조회 시 default 파이프라인 데이터 초기화 되지 않도록 처리
    if (formType === FormType.UPDATE && isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // 이 외 카테고리 select 수정 시 파이프라인 초기화
    setValue('pipelineCd', '');
  }, [watch('bnSubCatCd'), setValue, formType]);

  useEffect(() => {
    if (formType === FormType.UPDATE) {
      setFile([getValues('thumbPath')]);
    }
  }, []);

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Box sx={{ flex: 7 }}>
        <FormInnerContainer>
          <FormLabel>{t('common.form.section.basicInfo')}</FormLabel>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem size={2}>
              <RHFSelect
                label={t('business.form.bnSubCatCd')}
                name="bnSubCatCd"
                options={CTEGRY_SELECT_OPTIONS}
                useAll={false}
              />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFSelect
                label={t('business.form.pipelineCd')}
                name="pipelineCd"
                options={PIPELINE_SELECT_OPTIONS()}
                useAll={false}
              />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr 1fr' }}>
            <FormItem>
              <RHFSelect
                label={t('business.form.useYn')}
                name="useYn"
                options={getUseYn()}
                useAll={false}
              />
            </FormItem>
            <RHFSelect
              label={t('business.form.lang')}
              name="lang"
              useAll={false}
              options={getLang()}
            />
          </FormRow>
        </FormInnerContainer>
      </Box>
      <Box sx={{ overflow: 'hidden' }}>
        <FormInnerContainer sx={{ minHeight: '216px', justifyContent: 'center' }}>
          <FormLabel>{t('business.form.thumbPath')}</FormLabel>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFUploadImage
                name="thumbPath"
                maxFiles={1}
                files={file}
                onChange={(newFile: Array<File | string>) => {
                  setFile(newFile);
                  setValue('thumbPath', newFile[0], {
                    shouldValidate: true, // validation 즉시 실행
                    shouldDirty: true, // 값 변경 즉시 반영
                  });
                }}
              />
              {errors.thumbPath?.message && (
                <UploadErrorMessage message={String(errors.thumbPath.message)} />
              )}
            </FormItem>
          </FormRow>
        </FormInnerContainer>
      </Box>
    </Box>
  );
});
